// The inline survey's BI vocabulary, in one place.
//
// The card had an impression (`useSurveyShownBi`) and a conversion (`survey_submitted`) and nothing in
// between, so the only answerable question was "how many asks get answered" — never WHERE the ones that
// are not lose the reader. Every event below is one step of the same funnel, keyed by `ask_id` so the
// steps join: shown → page turns → picks and write-ins → skip / dismiss / reopen → submitted.
//
// ⚠️ **No prompt, no option label, no typed text ever rides one of these events.** All three are
// model-authored from the caller's own context and routinely name their contacts, clients and staff, so
// shipping them would move third-party personal names into the analytics warehouse as a side effect of
// counting a tap. That rule is already written on the submit event and on the shown hook; these carry
// INDICES and COUNTS instead, which answer every question the labels would and belong to nobody. The
// `message_id` on the shown event remains the way back to the words for anyone entitled to read them.
//
// THE TWO EVENTS THE CATALOGUE REGISTERS, and which field carries what:
//
// - **`itemSeen` (198:6)** — the card was offered and is on screen. `componentName` is
//   {@link SURVEY_COMPONENT}, the entity is the CONVERSATION, and the offered options ride
//   `option_ids` / `option_count` (see {@link surveyOffer}).
// - **`clickCta` (198:10)** — one step of the card. `componentName` is {@link SURVEY_COMPONENT} for
//   every step, so the whole widget is one component in the catalogue; WHICH thing was clicked is the
//   `entityType` ({@link SURVEY_TARGET}), and the step's own long name rides `step`. The entity is the
//   ASK — `{ id: askId, type: target }` — because the id and the type of one entity have to describe
//   the SAME thing: pairing the conversation's id with `option` publishes an `entities` row claiming
//   the conversation IS an option. The conversation stays where every step already carries it, in
//   `conversation_id`, which is what the funnel joins on.

/** The one component name every survey event reports — the card itself, on every surface. */
export const SURVEY_COMPONENT = 'survey_modal';

/**
 * The impression's `step`. Deliberately NOT a member of {@link SURVEY_BI}: every name in there is a
 * CLICK, and each one has to answer {@link SURVEY_STEP_TARGET} with the part of the card it was a click
 * on — which an impression, having been clicked on nothing, cannot. It keeps the name it had as the old
 * loadFinish region, so a query written against the funnel's first step still finds it.
 */
export const SURVEY_SHOWN_STEP = 'chat-survey-shown';

/**
 * WHAT was clicked, as the click's `entityType` — the survey's own small vocabulary, deliberately not
 * one of `ENTITY_TYPE`'s FQDNs (an option is not a business entity anyone can look up; it is the part
 * of the card the finger landed on). Shared 1:1 with iOS `SurveyBI.Target` and Android
 * `SurveyBi.Target`.
 */
export const SURVEY_TARGET = {
  /** A suggested answer. */
  option: 'option',
  /** The write-your-own field. */
  openAnswer: 'open_answer',
  /** Skip, on one question. */
  skip: 'skip',
  /** Next / Previous / the pager / the beat after a pick. */
  otherPage: 'move_to_other_page',
  /** The X, or a submit with nothing answered anywhere. */
  dismiss: 'dismiss',
  /** The settled prompts, tapped to take the ask back. */
  reopen: 'reopen',
  /** The answer was sent. */
  submit: 'submit',
} as const;

/** One name per step. Values are the `button_name` on the click event; the impression's own step name is
 *  {@link SURVEY_SHOWN_STEP}. */
export const SURVEY_BI = {
  /** A suggested answer was chosen (or, on a multiSelect question, un-chosen). */
  picked: 'survey_answer_picked',
  /** The first character typed into a question's write-your-own field — once per question. */
  wroteIn: 'survey_write_in_started',
  /** The reader moved between questions: Next, the pager, or the beat after a pick. */
  pageTurned: 'survey_page_turned',
  /** Skip on one question — the question, not the survey. */
  skipped: 'survey_question_skipped',
  /** The X, or a submit with nothing answered anywhere: the card settled without an answer. */
  dismissed: 'survey_dismissed',
  /** The prompts in the transcript were tapped to take a dismissed ask back. */
  reopened: 'survey_reopened',
  /** The answer was sent. Already fired before this module existed; its name is unchanged. */
  submitted: 'survey_submitted',
} as const;

/**
 * The part of the card each step was a click ON — the step's `entityType`. One entry per
 * {@link SURVEY_BI} step, so a new step cannot be added without saying what it was clicked on.
 */
// Declared as `Record<string, string>` so a step name in hand can be looked up, and CHECKED against
// the step vocabulary by the `satisfies` below — a new step with no target fails to compile.
export const SURVEY_STEP_TARGET: Record<string, string> = {
  [SURVEY_BI.picked]: SURVEY_TARGET.option,
  [SURVEY_BI.wroteIn]: SURVEY_TARGET.openAnswer,
  [SURVEY_BI.pageTurned]: SURVEY_TARGET.otherPage,
  [SURVEY_BI.skipped]: SURVEY_TARGET.skip,
  [SURVEY_BI.dismissed]: SURVEY_TARGET.dismiss,
  [SURVEY_BI.reopened]: SURVEY_TARGET.reopen,
  [SURVEY_BI.submitted]: SURVEY_TARGET.submit,
} satisfies Record<(typeof SURVEY_BI)[keyof typeof SURVEY_BI], string>;

/** What moved the reader off a question. `auto` is the beat after a single-select pick. */
export type SurveyPageTrigger = 'next' | 'previous' | 'auto' | 'skip';

/** Why the card settled unanswered — an explicit close, or every question skipped in turn. */
export type SurveyDismissReason = 'closed' | 'nothing_answered';

type Question = { id: string; options: readonly { id: string }[] };
type Survey = { askId: string; questions: readonly Question[] };
type Draft = { optionIds: Record<string, readonly string[]>; freeText: Record<string, string> };

/** 1-based position of a question in the card, as a string. 0 when the id is not one of its questions. */
export function questionIndex(survey: Survey, questionId: string): string {
  return String(survey.questions.findIndex((q) => q.id === questionId) + 1);
}

/**
 * 1-based position of an option within its question — or `'other'` for the synthetic write-your-own
 * row, which is deliberately not one of the offered options and has no index of its own.
 */
export function optionIndex(question: Question, optionId: string): string {
  const at = question.options.findIndex((o) => o.id === optionId);
  return at === -1 ? 'other' : String(at + 1);
}

/**
 * WHAT was offered, for the impression event: the shape of the ask, and the options it put in front of
 * the reader — as their harness-minted ids (`opt_1`, `opt_2`, …), never their labels.
 *
 * The catalogue asks for the options as the event's `entities`, and they cannot go there: the platform's
 * `entities` is a `Record<entityType, entityId>` — ONE id per type — so a list of N options collapses to
 * whichever one was written last. They ride `option_ids` instead, in the question order they were drawn
 * in, with `option_count` for anyone who only needs the size. `entities` keeps the one entity a survey
 * event genuinely has: the conversation it was offered in.
 *
 * Every id is scoped by its question (`1:opt_1`), because option ids repeat across questions — the
 * harness mints `opt_1` afresh for each one, so a flat list of them says nothing about which question
 * offered what.
 */
export function surveyOffer(survey: Survey): Record<string, string> {
  const ids: string[] = [];
  for (const [at, question] of survey.questions.entries()) {
    for (const option of question.options) ids.push(`${at + 1}:${option.id}`);
  }
  return {
    total_questions: String(survey.questions.length),
    option_count: String(ids.length),
    option_ids: ids.join(','),
  };
}

/**
 * How far the reader actually got, as counts: the size of the ask, how many questions carry an answer,
 * and the split between tapping and typing.
 *
 * The split is the point. "Answered" alone cannot tell a card whose suggestions fit from one whose
 * reader had to write their way past them — and which of the two it is decides whether the model is
 * asking good questions or the options are noise, which is the whole reason the write-in row exists.
 */
export function surveyProgress(survey: Survey, draft: Draft): Record<string, string> {
  let picked = 0;
  let written = 0;
  let answered = 0;
  for (const question of survey.questions) {
    const ids = (draft.optionIds[question.id] ?? []).filter((id) => question.options.some((o) => o.id === id));
    // Trim decides emptiness only — the same rule `buildSurveyAnswer` counts by, so the funnel's last
    // step and the answer it ships can never disagree about what "answered" meant.
    const typed = (draft.freeText[question.id] ?? '').trim().length > 0;
    if (ids.length > 0) picked++;
    if (typed) written++;
    if (ids.length > 0 || typed) answered++;
  }
  return {
    total_questions: String(survey.questions.length),
    answered_questions: String(answered),
    picked_answers: String(picked),
    written_answers: String(written),
  };
}
